import { useEffect, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MarkerType,
  ReactFlowProvider,
  type Edge,
  type Node,
  type NodeTypes,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { AIModel } from '@/types';
import CollabFlowNode from './CollabFlowNode';
import type { CollabConfig } from './collabTypes';
import {
  getCollabCanvasDescription,
  getCollabCanvasTitle,
  getCollabRoleTitle,
  getDeepResearchNodes,
  getModelNameById,
  getNodeSettingsSelectable,
  getCollabDrafterNodes,
  getCollabEditorNode,
  normalizeCollabConfig,
} from './collabTypes';

const nodeTypes: NodeTypes = {
  collab: CollabFlowNode,
};

interface CollabFlowCanvasProps {
  config: CollabConfig;
  models: AIModel[];
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  onChange: (config: CollabConfig) => void;
  onAddDrafter: () => void;
}

const createEdge = (id: string, source: string, target: string): Edge => ({
  id,
  source,
  target,
  type: 'smoothstep',
  animated: true,
  markerEnd: { type: MarkerType.ArrowClosed },
});

const createFlowEdges = (config: CollabConfig): Edge[] => {
  if (config.workflow === 'deep_research') {
    return [
      createEdge('start-orchestrator', 'start', 'orchestrator'),
      createEdge('orchestrator-planner', 'orchestrator', 'planner'),
      createEdge('planner-web', 'planner', 'web_research'),
      createEdge('planner-academic', 'planner', 'academic_research'),
      createEdge('planner-code', 'planner', 'code_analysis'),
      createEdge('planner-memory', 'planner', 'knowledge_graph'),
      createEdge('web-verify', 'web_research', 'source_verification'),
      createEdge('academic-verify', 'academic_research', 'source_verification'),
      createEdge('code-verify', 'code_analysis', 'source_verification'),
      createEdge('memory-verify', 'knowledge_graph', 'source_verification'),
      createEdge('verify-timeline', 'source_verification', 'timeline_builder'),
      createEdge('verify-citations', 'source_verification', 'citation_manager'),
      createEdge('timeline-report', 'timeline_builder', 'report_writer'),
      createEdge('citations-report', 'citation_manager', 'report_writer'),
      createEdge('report-output', 'report_writer', 'output'),
    ];
  }

  const drafters = getCollabDrafterNodes(config);
  const editor = getCollabEditorNode(config);

  return [
    ...drafters.map((node) => createEdge(`start-${node.id}`, 'start', node.id)),
    ...(editor ? drafters.map((node) => createEdge(`${node.id}-${editor.id}`, node.id, editor.id)) : []),
    ...(editor ? [createEdge(`${editor.id}-output`, editor.id, 'output')] : []),
  ];
};

const toFlowNodes = (config: CollabConfig, models: AIModel[], selectedNodeId: string | null): Node[] =>
  config.nodes.map((node) => ({
    id: node.id,
    type: 'collab',
    position: node.position,
    draggable: true,
    selectable: true,
    selected: node.id === selectedNodeId,
    data: {
      role: node.role,
      title: getCollabRoleTitle(node.role),
      subtitle: getNodeSettingsSelectable(node.role)
        ? getModelNameById(models, node.modelId)
        : node.role === 'start'
          ? config.workflow === 'deep_research'
            ? 'Research request enters orchestration'
            : 'Prompt fans out to drafters'
          : node.role === 'output'
            ? config.workflow === 'deep_research'
              ? 'Auto-published report output'
              : 'Final editor answer'
            : 'Configured stage',
    },
  }));

function CollabFlowCanvasInner({
  config,
  models,
  selectedNodeId,
  onSelectNode,
  onChange,
  onAddDrafter,
}: CollabFlowCanvasProps) {
  const normalizedConfig = useMemo(() => normalizeCollabConfig(config, models), [config, models]);
  const nodes = useMemo(() => toFlowNodes(normalizedConfig, models, selectedNodeId), [normalizedConfig, models, selectedNodeId]);
  const edges = useMemo(() => createFlowEdges(normalizedConfig), [normalizedConfig]);
  const researchNodeCount = useMemo(() => getDeepResearchNodes(normalizedConfig).length, [normalizedConfig]);

  useEffect(() => {
    if (selectedNodeId && !normalizedConfig.nodes.some((node) => node.id === selectedNodeId)) {
      onSelectNode(null);
    }
  }, [normalizedConfig.nodes, onSelectNode, selectedNodeId]);

  return (
    <div className="flex h-full min-h-[420px] flex-col rounded-xl border bg-muted/20">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold">{getCollabCanvasTitle(normalizedConfig.workflow)}</h3>
          <p className="text-xs text-muted-foreground">{getCollabCanvasDescription(normalizedConfig.workflow)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">Drag nodes to rearrange</Badge>
          {normalizedConfig.workflow === 'deep_research' ? (
            <Badge variant="secondary">{researchNodeCount} research roles</Badge>
          ) : (
            <Button type="button" size="sm" variant="outline" onClick={onAddDrafter}>
              <Plus className="mr-1 h-4 w-4" />
              Add drafter
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          nodesDraggable
          nodesConnectable={false}
          elementsSelectable
          panOnScroll
          zoomOnScroll
          onNodeClick={(_, node) => onSelectNode(node.id)}
          onPaneClick={() => onSelectNode(null)}
          onNodesChange={(changes) => {
            const positionChanges = changes.filter((change) => change.type === 'position') as Array<{
              id: string;
              position?: { x: number; y: number };
            }>;
            if (positionChanges.length === 0) return;

            onChange({
              ...normalizedConfig,
              nodes: normalizedConfig.nodes.map((node) => {
                const change = positionChanges.find((item) => item.id === node.id);
                if (!change?.position) return node;
                return {
                  ...node,
                  position: change.position,
                };
              }),
            });
          }}
        >
          <Background gap={20} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
}

export default function CollabFlowCanvas(props: CollabFlowCanvasProps) {
  return (
    <ReactFlowProvider>
      <CollabFlowCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
